/**
 * Jobs API — typed wrapper over the `/jobs/*` endpoints. Phase A3
 * of the API isolation plan (`/app/memory/FRONTEND_ARCHITECTURE_AUDIT.md`).
 *
 * Every function here:
 *   • returns the unwrapped response data (not the full Axios
 *     response object) for the happy path
 *   • throws the raw Axios error on failure so callers can keep
 *     reading `err.response?.status` exactly like before — use
 *     `extractMessage(err)` from `./errors` to get a user-facing
 *     string.
 *
 * Endpoints covered (all `/api/jobs/*`):
 *   GET    /jobs[?limit=N]    (cached via apiCache, TTL 60s)        listJobs
 *   POST   /jobs                                                    createJob
 *   GET    /jobs/{id}                                               getJob
 *   PUT    /jobs/{id}                                               updateJob
 *   PUT    /jobs/{id} (status only)                                 updateJobStatus
 *   GET    /jobs/filter-options                                     getFilterOptions
 *   GET    /jobs/{id}/email-recipients                              getEmailRecipients
 *   GET    /jobs/{id}/change-order-requests                         getChangeOrderRequests
 *   GET    /jobs/{id}/crews                                         getJobCrews
 *   PUT    /jobs/{id}/weather-zip                                   setWeatherZipOverride
 *   GET    /jobs/{id}/reports                                       getWalkReports
 *   POST   /jobs/{id}/reports                                       createWalkReport
 *
 * NOT covered here (deliberately — adjacent domains):
 *   • `/companycam/jobs/...` — CompanyCam domain (api/integrations/* later)
 *   • `/job-reports/...` (templates) — Job Templates domain (separate)
 *
 * cachedGet vs apiClient
 * ----------------------
 * `listJobs()` deliberately routes through `cachedGet` (a separate
 * cache abstraction in `lib/apiCache.js`), because every consumer of
 * the jobs list relies on its 60-second TTL + in-flight dedup for
 * perf. Folding `cachedGet` into `apiClient` is a future phase (A5);
 * for now `listJobs` preserves exact behaviour. Cache invalidation
 * after job mutations works automatically — `lib/apiCacheInterceptor.js`
 * already invalidates `/api/jobs` on any POST/PUT/PATCH/DELETE to
 * that path, regardless of which axios call made the mutation.
 */
import { apiClient } from './client';
import { cachedGet } from '../lib/apiCache';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Cached list of jobs.
 *
 * @param {object} [opts]
 * @param {number} [opts.limit]     Optional `?limit=N` query param.
 * @param {number} [opts.ttlMs=60000] Cache TTL (override the default).
 * @param {boolean} [opts.force]    If true, bypass cache by setting ttlMs=0.
 * @returns {Promise<{ data: Array, fromCache: boolean }>}
 *
 * Returns the SAME `{ data, fromCache }` shape as a direct `cachedGet`
 * call so the 11 existing consumers can migrate with a one-line edit
 * and zero behaviour change. The cache key (full URL) is identical
 * to what the consumers were producing inline, so the cache slot is
 * shared — no double-fetches across pages.
 */
export async function listJobs({ limit, ttlMs = 60_000, force = false, includeArchived = false, excludeCompleted = true, fields } = {}) {
  const qs = new URLSearchParams();
  if (limit) qs.set('limit', String(limit));
  if (includeArchived) qs.set('include_archived', 'true');
  // Hide jobs whose Strata status is 'Complete' from every operational
  // picker that uses this helper (Workflows, Material Requests, Media,
  // Reports, Estimates, Relay, template dialogs). Callers that need
  // completed jobs can pass `excludeCompleted: false`. The main Jobs
  // board uses `listJobsPaged` (below), so it is unaffected.
  if (excludeCompleted) qs.set('exclude_completed', 'true');
  // `summary` → only id / name / client_name / source (light lists).
  if (fields) qs.set('fields', fields);
  const tail = qs.toString();
  const url = `${API}/jobs${tail ? `?${tail}` : ''}`;
  return cachedGet(url, { ttlMs: force ? 0 : ttlMs });
}

/**
 * iter-156 Phase 3 — server-side paginated + filtered Jobs board.
 *
 * Unlike `listJobs` (which returns the full unwrapped array for job
 * pickers across the app), this hits `GET /jobs?paginate=true` and
 * returns the `PageResult` envelope:
 *   { items, total, page, page_size, has_more }
 *
 * ALL filtering (search, status, divisions, crews, tags, status
 * buckets) is pushed to Mongo so the client only ever holds one page.
 * Not cached — the board wants fresh results on every page / filter
 * change, and the cache interceptor already invalidates `/api/jobs`
 * on any mutation.
 */
export async function listJobsPaged({
  page = 1,
  pageSize = 50,
  search = '',
  status = 'all',
  divisions = [],
  crews = [],
  tags = [],
  statusBuckets = [],
  includeArchived = false,
} = {}) {
  const qs = new URLSearchParams();
  qs.set('paginate', 'true');
  qs.set('page', String(page));
  qs.set('page_size', String(pageSize));
  if (search) qs.set('search', search);
  if (status && status !== 'all') qs.set('status', status);
  if (divisions?.length) qs.set('divisions', divisions.join(','));
  if (crews?.length) qs.set('crews', crews.join(','));
  if (tags?.length) qs.set('tags', tags.join(','));
  if (statusBuckets?.length) qs.set('status_buckets', statusBuckets.join(','));
  if (includeArchived) qs.set('include_archived', 'true');
  const { data } = await apiClient.get(`/jobs?${qs.toString()}`);
  return data;
}

/**
 * iter-156gr — Archive a job (soft-hide from the Jobs board).
 * Owner / admin tier only on the backend.  Reversible via
 * `unarchiveJob`.
 */
export async function archiveJob(id) {
  const { data } = await apiClient.post(`/jobs/${id}/archive`);
  return data;
}

export async function unarchiveJob(id) {
  const { data } = await apiClient.post(`/jobs/${id}/unarchive`);
  return data;
}

/**
 * Per-job "Pull from Aspire" — re-imports THIS one job's opportunity
 * (status/dates/budget/name) + financials (invoices, payments, work
 * tickets & revenues). Rate-limited server-side to one pull/job/60s
 * (429 → err.response.data.detail.retry_after). Returns { ok, job, result }.
 */
export async function pullJobFromAspire(id) {
  const { data } = await apiClient.post(`/jobs/${id}/pull-aspire`, {});
  return data;
}

/**
 * Create a new job. Returns the created job document.
 */
export async function createJob(payload) {
  const { data } = await apiClient.post('/jobs', payload);
  return data;
}

/**
 * Fetch a single job by id. Optional axios config (e.g. `{ timeout }`)
 * passes through to the underlying axios call — the JobDetailPage
 * supplies a `timeout` to keep the slow-S3-blob fetch from blocking
 * the page forever.
 */
export async function getJob(id, config) {
  const { data } = await apiClient.get(`/jobs/${id}`, config);
  return data;
}

/**
 * Update a job. Returns the updated document. Callers pass a partial
 * patch — server merges.
 */
export async function updateJob(id, patch) {
  const { data } = await apiClient.put(`/jobs/${id}`, patch);
  return data;
}

/**
 * Status-only update. Thin convenience wrapper around `updateJob`
 * for inline status pills on the job cards.
 */
export async function updateJobStatus(id, status) {
  return updateJob(id, { status });
}

/**
 * Get the filter facet metadata (status_map, tags, regions, …)
 * used by the Jobs list filter bar + Material Requests filter chips.
 */
export async function getFilterOptions() {
  const { data } = await apiClient.get('/jobs/filter-options');
  return data;
}

/**
 * In-progress jobs with lat/lng for the map view.
 * Returns { jobs: [{id,name,client_name,address,city,state,zip_code,
 * status,estimated_budget,latitude,longitude}], total_in_progress, mapped }.
 */
export async function getJobMapLocations(config) {
  const { data } = await apiClient.get('/jobs/map-locations', config);
  return data;
}

/**
 * Get email recipient suggestions for a job (client + primary
 * contacts + manager). Used by EmailClientReportDialog and the
 * Send-via-Gmail flow.
 */
export async function getEmailRecipients(jobId) {
  const { data } = await apiClient.get(`/jobs/${jobId}/email-recipients`);
  return data;
}

/**
 * Fetch the change-order request list for a job. Returns the array
 * already unwrapped (handles both `{ requests: [...] }` and bare
 * array responses).
 */
export async function getChangeOrderRequests(jobId, config) {
  const { data } = await apiClient.get(
    `/jobs/${jobId}/change-order-requests`,
    config,
  );
  return data;
}

/**
 * Fetch the crews currently assigned to a job. Returns the
 * response data as-is (caller unwraps `data.crews` if needed).
 */
export async function getJobCrews(jobId) {
  const { data } = await apiClient.get(`/jobs/${jobId}/crews`);
  return data;
}

/**
 * Persist the user's manual weather-zip override on the job doc.
 * Used by ClientReportPage's weather card.
 */
export async function setWeatherZipOverride(jobId, weatherZip) {
  const { data } = await apiClient.put(
    `/jobs/${jobId}/weather-zip`,
    { weather_zip_override: weatherZip },
  );
  return data;
}

/**
 * iter-156dx — Persist the per-job Client Report date-range filter.
 * Payload: { mode: 'last_7_days'|'last_30_days'|'lifecycle'|'custom',
 *           start?: 'YYYY-MM-DD', end?: 'YYYY-MM-DD' }
 * `start`/`end` are required only when `mode === 'custom'`.
 */
export async function setClientReportRange(jobId, range) {
  const { data } = await apiClient.put(
    `/jobs/${jobId}/client-report-range`,
    range,
  );
  return data;
}

/**
 * iter-156eh — Per-job recipients for the Weekly Client Reports
 * automation. List of `{name, email}` objects. Empty list = silent
 * generation (no email blast; PM in-app notification only).
 */
export async function setWeeklyEmailRecipients(jobId, recipients) {
  const { data } = await apiClient.put(
    `/jobs/${jobId}/weekly-email-recipients`,
    { recipients },
  );
  return data;
}

/**
 * Fetch walk-report list for a job. Returns the response body
 * as-is (consumers read `.reports` themselves).
 */
export async function getWalkReports(jobId, config) {
  const { data } = await apiClient.get(`/jobs/${jobId}/reports`, config);
  return data;
}

/**
 * Company-wide list of every field report across all accessible jobs.
 * Returns { reports: [...] } with each row carrying `job_name`.
 */
export async function getAllJobReports(config) {
  const { data } = await apiClient.get('/job-reports', config);
  return data;
}

/**
 * Create a new walk report on a job. Server assigns the id +
 * template-derived schema. Returns the created doc.
 */
export async function createWalkReport(jobId, reportType, templateIds = null) {
  const body = { report_type: reportType };
  const ids = Array.isArray(templateIds)
    ? templateIds.filter(Boolean)
    : (templateIds ? [templateIds] : []);
  if (ids.length === 1) body.template_id = ids[0];
  else if (ids.length > 1) body.template_ids = ids;
  const { data } = await apiClient.post(`/jobs/${jobId}/reports`, body);
  return data;
}

/**
 * Read this job's Aspire payment-milestone snapshot. Returns the full
 * payload `{ milestones: [...], drifted_count, new_from_co_count,
 * first_sync, ... }` — the MilestoneSideRail consumer reads every
 * field, so we keep the shape opaque here.
 *
 * No cache layer in this wrapper — the rail keeps its own in-memory
 * `MILESTONE_CACHE` (Map) for sub-second open/close UX, and the panel
 * always wants fresh data on mount.
 */
export async function getPaymentMilestones(jobId, config) {
  const { data } = await apiClient.get(
    `/jobs/${jobId}/payment-milestones`,
    config,
  );
  return data;
}

/**
 * Force a re-sync with Aspire. Server fetches the latest billing rows,
 * applies CO additions / drift detection, and returns the refreshed
 * snapshot. Same payload shape as `getPaymentMilestones`.
 */
export async function refreshPaymentMilestones(jobId) {
  const { data } = await apiClient.post(
    `/jobs/${jobId}/payment-milestones/refresh`,
    {},
  );
  return data;
}


/**
 * Project Checkpoints (Pre-Construction / Material Selections /
 * Post-Construction) — derived from the job's signed reports.
 * Returns `{ enabled, checkpoints: [{type,label,status,overdue,report_id}] }`.
 */
export async function getJobCheckpoints(jobId, config) {
  const { data } = await apiClient.get(`/jobs/${jobId}/checkpoints`, config);
  return data;
}

/** Company-wide roll-up of overdue checkpoints for the dashboard alert. */
export async function getDashboardCheckpoints(config) {
  const { data } = await apiClient.get('/dashboard/checkpoints', config);
  return data;
}

/** Company-wide workflow settings (track_checkpoints, sequential_default). */
export async function getWorkflowSettings(config) {
  const { data } = await apiClient.get('/company/workflow-settings', config);
  return data;
}

export async function updateWorkflowSettings(patch) {
  const { data } = await apiClient.put('/company/workflow-settings', patch);
  return data;
}
